import { HeroSection } from '@/components/sections/hero-section';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  BookOpen, 
  Users, 
  GraduationCap, 
  Globe,
  Heart,
  Play,
  Sparkles,
  Target
} from 'lucide-react';
import { Link } from 'react-router-dom';

function MissionSection() {
  return (
    <section id="mission" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5">
            <Target className="mr-1 h-3 w-3 text-primary" />
            Our Vision
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
            Raising Leaders That<br />
            <span className="block text-primary">Transform Society</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="p-6 rounded-xl bg-primary/5 border border-primary/10">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Presence & Wisdom</h3>
              <p className="text-muted-foreground text-sm">
                A strong quest for God's presence and seeking divine insight for practical leadership.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-primary/5 border border-primary/10">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Excellence & Integrity</h3>
              <p className="text-muted-foreground text-sm">
                An uncompromising stand for high standards and a commitment to righteousness.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-primary/5 border border-primary/10">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Purpose & Destiny</h3>
              <p className="text-muted-foreground text-sm">
                Helping individuals discover and deploy their potential to impact their world.
              </p>
            </div>
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Dominion Mandate exists to raise up leaders who understand their 
            God-given mandate to influence every sphere of society for His glory. We are 
            committed to the total transformation of the man — spiritual, mental, and social.
          </p>
        </div>
      </div>
    </section>
  );
}

function PastorSection() {
  return (
    <section id="about" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto m-auto">
          {/* Rev David's Photo - Circular */}
          <div className="relative flex justify-center w-fit justify-self-center lg:justify-end lg:justify-self-end">
            <div className="relative">
              {/* Circular photo frame */}
              <div className="w-96 h-96 md:w-[28rem] md:h-[28rem] rounded-full overflow-hidden border-4 border-primary/20 shadow-2xl">
                <img
                  src="/image.png"
                  alt="Dr. David Ogbueli - Founder & President of Dominion Mandate"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-primary/20 blur-xl" />
            </div>
          </div>

          {/* Content */}
          <div className="max-w-xl mx-auto lg:mx-0 lg:text-left text-center">
            <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5">
              <Heart className="mr-1 h-3 w-3 text-primary" />
              Our Founder
            </Badge>
            
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
              A Vision to Raise
              <span className="block text-primary">Societal Transformers</span>
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground">
              <p>
                Dr. David Ogbueli is the Founder and President of Dominion Mandate and the Golden Heart Foundation. 
                He is a minister of the Gospel, social reformer, and an icon of transformational leadership.
              </p>
              <p>
                He is a Management and Government Consultant who speaks internationally in organizations, 
                government houses, embassies and churches. His mission is to raise leaders that transform society 
                by helping individuals discover their purpose and deploy their potential.
              </p>
              <p>
                Dr. Ogbueli's ministry is built on foundational truths including a quest for God's Presence, 
                the Wisdom of God, Excellence, Integrity, and Healthy Relationships.
              </p>
            </div>

            <div className="mt-8">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                <Link to="/about/pastor">
                  Learn More About Dr. Ogbueli
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MinistryOverviewSection() {
  const ministries = [
    {
      icon: BookOpen,
      title: 'GMTV - Gospel Message Television',
      description: '24/7 Christian broadcasting reaching millions globally with powerful gospel messages and worship.',
      link: '#',
    },
    {
      icon: GraduationCap,
      title: 'International School of Ministry',
      description: 'Comprehensive biblical education with accredited programs for pastors, leaders, and serious students.',
      link: '#',
    },
    {
      icon: Users,
      title: 'Mentorship Program',
      description: 'One-on-one and group mentorship opportunities with experienced leaders and pastors.',
      link: '#',
    },
    {
      icon: Globe,
      title: 'Online Community',
      description: 'Connect with believers worldwide through our interactive platform, forums, and virtual gatherings.',
      link: '#',
    }
  ];

  return (
    <section id="ministries" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5">
            <Sparkles className="mr-1 h-3 w-3 text-primary" />
            Our Ministries
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Comprehensive
            <span className="block text-primary">Ministry Platform</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need for spiritual growth and leadership development, 
            designed to help you fulfill your divine mandate.
          </p>
        </div>

        {/* Ministries Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {ministries.map((ministry, index) => (
            <Card 
              key={index}
              className="group relative border-border bg-card hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 overflow-hidden"
            >
              {/* Subtle accent */}
              <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <CardContent className="p-6 relative z-10">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl bg-primary/10 shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <ministry.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {ministry.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {ministry.description}
                    </p>
                  </div>
                </div>
              </CardContent>
              
              {/* Hover arrow */}
              <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ArrowRight className="h-5 w-5 text-primary" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-24 bg-primary relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
          Ready to Begin Your Journey?
        </h2>
        <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-10">
          Join our community of believers committed to spiritual growth, biblical truth, 
          and transforming their world for Christ.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="lg" 
            className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 text-lg px-8 h-14 font-semibold shadow-2xl"
            asChild
          >
            <Link to="/auth/signup">
              Register Now
            </Link>
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            className="text-lg px-8 h-14 border-primary-foreground/30 bg-primary-foreground/5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            asChild
          >
            <Link to="/signin">
              <Play className="mr-2 h-5 w-5" />
              Sign In
            </Link>
          </Button>
        </div>

        {/* Trust indicators */}
        <div className="mt-12 flex flex-wrap gap-8 text-primary-foreground/60 mx-auto w-fit">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            <span>Global Community</span>
          </div>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            <span>Biblical Teaching</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <span>Mentorship Available</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// About Dr David section (moved from PastorSection)
function AboutDrDavidSection() {
  return (
    <section id="about-dr-david" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          {/* Dr David's Photo - Circular */}
          <div className="relative flex justify-center w-fit justify-self-center lg:justify-end lg:justify-self-end">
            <div className="relative">
              {/* Circular photo frame */}
              <div className="w-96 h-96 md:w-[28rem] md:h-[28rem] rounded-full overflow-hidden border-4 border-primary/20 shadow-2xl">
                <img
                  src="/image.png"
                  alt="Dr. David Ogbueli - Founder & President of Dominion Mandate"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-primary/20 blur-xl" />
            </div>
          </div>

          {/* Content */}
          <div className="max-w-xl mx-auto lg:mx-0 lg:text-left text-center">
            <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5">
              <Heart className="mr-1 h-3 w-3 text-primary" />
              Our Founder
            </Badge>

            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
              A Vision to Raise
              <span className="block text-primary">Societal Transformers</span>
            </h2>

            <div className="space-y-6 text-lg text-muted-foreground">
              <p>
                Dr. David Ogbueli is the Founder and President of Dominion Mandate and the Golden Heart Foundation.
                He is a minister of the Gospel, social reformer, and an icon of transformational leadership.
              </p>
              <p>
                He is a Management and Government Consultant who speaks internationally in organizations,
                government houses, embassies and churches. His mission is to raise leaders that transform society
                by helping individuals discover their purpose and deploy their potential.
              </p>
              <p>
                Dr. Ogbueli's ministry is built on foundational truths including a quest for God's Presence,
                the Wisdom of God, Excellence, Integrity, and Healthy Relationships.
              </p>
            </div>

            <div className="mt-8">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                <Link to="/about">
                  Learn More About Dr. Ogbueli
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Ministries section (updated to match reference site)
function MinistriesSection() {
  const ministries = [
    {
      icon: BookOpen,
      title: 'GMTV - Gospel Message Television',
      description: '24/7 Christian broadcasting reaching millions globally with powerful gospel messages and worship.',
      link: '/media',
    },
    {
      icon: GraduationCap,
      title: 'International School of Ministry',
      description: 'Comprehensive biblical education with accredited programs for pastors, leaders, and serious students.',
      link: '/ministry',
    },
    {
      icon: Users,
      title: 'Mentorship Program',
      description: 'One-on-one and group mentorship opportunities with experienced leaders and pastors.',
      link: '/mentorship',
    },
    {
      icon: Globe,
      title: 'Global Ministry Network',
      description: 'Transforming communities worldwide through strategic partnerships and outreach initiatives.',
      link: '/ministry',
    }
  ];

  return (
    <section id="ministries" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5">
            <Sparkles className="mr-1 h-3 w-3 text-primary" />
            Our Ministries
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Comprehensive
            <span className="block text-primary">Ministry Platform</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need for spiritual growth and leadership development,
            designed to help you fulfill your divine mandate.
          </p>
        </div>

        {/* Ministries Grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {ministries.map((ministry, index) => (
            <Card
              key={index}
              className="group relative border-border bg-card hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 overflow-hidden"
            >
              {/* Subtle accent */}
              <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <CardContent className="p-6 relative z-10">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl bg-primary/10 shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <ministry.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {ministry.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {ministry.description}
                    </p>
                  </div>
                </div>
              </CardContent>

              {/* Hover arrow */}
              <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ArrowRight className="h-5 w-5 text-primary" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

// Media Center section
function MediaCenterSection() {
  return (
    <section id="media-center" className="py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5">
            <Play className="mr-1 h-3 w-3 text-primary" />
            Media Center
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Watch & Listen to
            <span className="block text-primary">Powerful Messages</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Experience the presence of God through our live services, recorded messages,
            and exclusive member content.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" className="bg-primary hover:bg-primary/90" asChild>
            <Link to="/watch">
              <Play className="mr-2 h-5 w-5" />
              Watch Live
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/media">
              Browse Messages
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

// Give section
function GiveSection() {
  return (
    <section id="give" className="py-24 bg-primary/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5">
            <Heart className="mr-1 h-3 w-3 text-primary" />
            Support Our Ministry
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Partner with Us in
            <span className="block text-primary">Kingdom Advancement</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Your generous giving enables us to continue spreading the gospel,
            training leaders, and transforming communities worldwide.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" className="bg-primary hover:bg-primary/90" asChild>
            <Link to="/give">
              Give Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/contact">
              Contact Us
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      <HeroSection />
    </div>
  );
}
